import React, { useEffect } from 'react';
import { useSEO } from '../context/SEOContext';
import { getFileUrl } from '../utils/helpers';

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  ogTitle, 
  ogDescription, 
  ogImage,
  ogType = 'website',
  canonicalUrl 
}) => {
  const { seoSettings } = useSEO();

  // Fallback to site settings if props not provided
  const pageTitle = title || seoSettings.siteTitle;
  const pageDescription = description || seoSettings.siteDescription;
  const pageKeywords = keywords || seoSettings.metaKeywords;
  const pageOgTitle = ogTitle || seoSettings.ogTitle || pageTitle;
  const pageOgDescription = ogDescription || seoSettings.ogDescription || pageDescription;
  const pageOgImage = ogImage || seoSettings.ogImage;
  const currentUrl = canonicalUrl || window.location.href;

  useEffect(() => {
    // Update document title
    document.title = pageTitle;

    // Update meta tags
    updateMetaTag('name', 'description', pageDescription);
    updateMetaTag('name', 'keywords', pageKeywords);
    
    // Open Graph tags
    updateMetaTag('property', 'og:title', pageOgTitle);
    updateMetaTag('property', 'og:description', pageOgDescription);
    updateMetaTag('property', 'og:type', ogType);
    updateMetaTag('property', 'og:url', currentUrl);
    if (pageOgImage) {
      updateMetaTag('property', 'og:image', pageOgImage);
      updateMetaTag('property', 'og:image:width', '1200');
      updateMetaTag('property', 'og:image:height', '630');
    }
    updateMetaTag('property', 'og:site_name', seoSettings.siteTitle);
    updateMetaTag('property', 'og:locale', 'tr_TR');

    // Twitter Card tags
    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', pageOgTitle);
    updateMetaTag('name', 'twitter:description', pageOgDescription);
    if (pageOgImage) {
      updateMetaTag('name', 'twitter:image', pageOgImage);
    }

    // Additional SEO tags
    updateMetaTag('name', 'robots', 'index, follow');
    updateMetaTag('name', 'googlebot', 'index, follow');
    updateMetaTag('name', 'language', 'Turkish');
    updateMetaTag('name', 'revisit-after', '7 days');
    updateMetaTag('name', 'author', 'Kuran-ı Kerim');

    // Canonical URL
    updateLinkTag('canonical', currentUrl);

    // Favicon
    if (seoSettings.favicon) {
      const faviconUrl = getFileUrl(seoSettings.favicon);
      updateLinkTag('icon', faviconUrl, 'image/x-icon');
      updateLinkTag('shortcut icon', faviconUrl, 'image/x-icon');
    }

    // Apple Touch Icon
    if (seoSettings.logo) {
      const logoUrl = getFileUrl(seoSettings.logo);
      updateLinkTag('apple-touch-icon', logoUrl);
    }

  }, [pageTitle, pageDescription, pageKeywords, pageOgTitle, pageOgDescription, pageOgImage, currentUrl, ogType, seoSettings]);

  return null; // This component doesn't render anything
};

// Helper function to update or create meta tags
const updateMetaTag = (attribute, key, content) => {
  if (!content) return;
  
  let element = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

// Helper function to update or create link tags
const updateLinkTag = (rel, href, type) => {
  if (!href) return;

  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
  if (type) {
    element.setAttribute('type', type);
  }
};

export default SEOHead;
